/* AQQA Studio V2 / Independent gallery and size assistant */
(() => {
  'use strict';
  const query = (selector, scope = document) => scope.querySelector(selector);
  const all = (selector, scope = document) => Array.from(scope.querySelectorAll(selector));
  const normalize = text => String(text ?? '').toLocaleLowerCase('tr-TR')
    .replace(/ı/g, 'i').replace(/ğ/g, 'g').replace(/ü/g, 'u')
    .replace(/ş/g, 's').replace(/ö/g, 'o').replace(/ç/g, 'c')
    .replace(/[^a-z0-9]+/g, ' ').trim();
  const matchingColor = (a, b) => Boolean(normalize(a)) && normalize(a) === normalize(b);
  const readTaggedColor = value => {
    const alt = String(value || '');
    // Add Shopify media alt text "Renk: Siyah | Önden görünüm" for explicit group assignment.
    const tagged = alt.match(/(?:^|[|;,])\s*(?:renk|color|colour)\s*[:=]\s*([^|;,]+)/i);
    return tagged ? tagged[1].trim() : '';
  };
  const escapeText = text => String(text).replace(/[&<>"']/g, c => ({'&':'&amp;', '<':'&lt;', '>':'&gt;', '"':'&quot;', "'":'&#39;'}[c]));

  function initializeGallery(section) {
    const colorIndex = Number(section.dataset.colorOptionIndex);
    const slides = all('[data-gallery-item]', section);
    const thumbs = all('[data-gallery-thumb]', section);
    const galleryData = query('[data-gallery-variant-map]', section);
    if (!galleryData || !slides.length) return;
    let variantData = [];
    try { variantData = JSON.parse(galleryData.textContent); } catch { /* gallery remains functional */ }

    const variantsById = new Map(variantData.map(v => [String(v.id), v]));
    const anchors = new Map();
    variantData.forEach(variant => {
      if (variant.color && variant.mediaId && !anchors.has(String(variant.mediaId))) {
        anchors.set(String(variant.mediaId), variant.color);
      }
    });

    // Color identification priority: image ALT tags > start-of-color gallery group
    // marked with variant-specific featured images > single safe fallback image.
    let runningGroup = '';
    const knownColors = [...new Set(variantData.map(v => v.color).filter(Boolean))];
    const detectFilenameColor = src => {
      let basename = '';
      try {basename = decodeURIComponent(String(src || '').split('?')[0].split('/').pop() || '');} catch { return ''; }
      const normalizedFilename = ' ' + normalize(basename.replace(/\.[^.]+$/, '')) + ' ';
      // Name recognition is only a convenience. Explicit ALT tags are always preferred.
      const matches = knownColors.filter(color => normalizedFilename.includes(' ' + normalize(color) + ' '));
      return matches.length === 1 ? matches[0] : '';
    };
    const media = slides.map(slide => {
      const id = String(slide.dataset.mediaId);
      const alt = slide.dataset.mediaAlt || '';
      const explicit = readTaggedColor(alt) || detectFilenameColor(slide.dataset.mediaUrl);
      if (anchors.has(id)) runningGroup = anchors.get(id);
      const group = explicit || runningGroup;
      if (explicit) runningGroup = explicit;
      return {id, slide, thumb: thumbs.find(t => String(t.dataset.mediaId) === id), group};
    });
    let visible = media;
    let activeIndex = 0;
    const counter = query('[data-gallery-counter]', section);
    const emptyNotice = query('[data-gallery-empty]', section);

    function showSlide(index) {
      if (emptyNotice) emptyNotice.hidden = visible.length > 0;
      if (!visible.length) {media.forEach(item => {item.slide.hidden = true; if(item.thumb)item.thumb.hidden = true;});if(counter)counter.hidden=true;return;}
      if(counter)counter.hidden=false;
      activeIndex = (index + visible.length) % visible.length;
      const active = visible[activeIndex];
      media.forEach(item => {
        item.slide.hidden = item !== active;
        if (item.thumb) item.thumb.setAttribute('aria-current', String(item === active));
      });
      if (counter) counter.textContent = `${String(activeIndex + 1).padStart(2,'0')} / ${String(visible.length).padStart(2,'0')}`;
      // Do not call scrollIntoView: it could move the entire product page on mobile.
    }

    function showColor(color, variantId) {
      const record = variantsById.get(String(variantId));
      const chosenColor = color || record?.color || '';
      if (colorIndex < 0 || !chosenColor) {
        visible = media;
      } else {
        const associated = media.filter(item => matchingColor(item.group, chosenColor));
        const featured = record?.mediaId ? media.find(item => item.id === String(record.mediaId)) : null;
        if (associated.length) {
          visible = associated;
          // Keep variant's featured image visible even if older, unlabelled images are interspersed.
          if (featured && !visible.includes(featured)) visible.unshift(featured);
        } else if (featured) {
          // No reliable color tags. Showing only the linked photo avoids mixing colorways.
          visible = [featured];
        } else {
          // Shopify cannot determine other image colors without assignment/ALT tags.
          visible = [];
        }
      }
      media.forEach(item => { if (item.thumb) item.thumb.hidden = !visible.includes(item); });
      const newFirst = record?.mediaId ? visible.findIndex(item => item.id === String(record.mediaId)) : -1;
      showSlide(newFirst < 0 ? 0 : newFirst);
    }

    thumbs.forEach(thumb => thumb.addEventListener('click', () => {
      const index = visible.findIndex(item => item.id === String(thumb.dataset.mediaId));
      if (index !== -1) showSlide(index);
    }));
    query('[data-gallery-prev]', section)?.addEventListener('click', () => showSlide(activeIndex - 1));
    query('[data-gallery-next]', section)?.addEventListener('click', () => showSlide(activeIndex + 1));

    const selectedId = query('[data-variant-id]', section)?.value;
    const initialVariant = variantsById.get(String(selectedId));
    const colorGroup = colorIndex >= 0 ? query(`[data-option-index="${colorIndex}"] input:checked`, section) : null;
    const initialColor = colorGroup?.value || initialVariant?.color || '';
    const initialRecord = variantData.find(item => matchingColor(item.color, initialColor));
    showColor(initialColor, initialVariant?.id || initialRecord?.id || selectedId);
    section.addEventListener('aqqa:variantchange', event => {
      const variant = event.detail?.variant;
      const color = event.detail?.color || '';
      const galleryId = event.detail?.galleryVariantId || variant?.id;
      const targetRecord = variantsById.get(String(galleryId));
      showColor(color || targetRecord?.color, galleryId);
    });
  }

  function initializeSizeTools(section) {
    const finder = query('[data-size-finder]', section);
    const chart = query('[data-size-chart-modal]', section);
    const cfg = query('[data-size-chart-config]', section);
    if (!finder || !chart || !cfg) return;
    let config;
    try { config = JSON.parse(cfg.textContent); } catch { return; }
    const parseChart = str => String(str || '').split(/[,;\n]+/).map(item => {
      const parts = item.trim().split(':');
      return {size:parts[0]?.trim(), max:Number(parts[1]?.trim())};
    }).filter(item => item.size && Number.isFinite(item.max) && item.max > 0).sort((a,b) => a.max - b.max);
    const charts = {men:parseChart(config.men), women:parseChart(config.women)};
    const renderChart = gender => {
      const tbody = query('[data-size-chart-rows]', chart);
      if (!tbody) return;
      tbody.innerHTML = (charts[gender] || []).map(row => `<tr><td>${escapeText(row.size)}</td><td>${row.max.toFixed(1).replace('.', ',')} cm</td></tr>`).join('');
    };
    const open = dialog => { if (typeof dialog.showModal === 'function' && !dialog.open) dialog.showModal(); };
    all('[data-open-size-finder]', section).forEach(button => button.addEventListener('click', () => open(finder)));
    all('[data-open-size-chart]', section).forEach(button => button.addEventListener('click', () => {renderChart(query('[data-chart-gender]', chart)?.value || 'men');open(chart);}));
    query('[data-close-size-finder]', finder)?.addEventListener('click', () => finder.close());
    query('[data-close-size-chart]', chart)?.addEventListener('click', () => chart.close());
    [finder, chart].forEach(dialog => dialog.addEventListener('click', e => {
      if (e.target === dialog) dialog.close();
    }));
    query('[data-chart-gender]', chart)?.addEventListener('change', event => renderChart(event.target.value));
    query('[data-size-calculator]', finder)?.addEventListener('submit', event => {
      event.preventDefault();
      const footField = query('[data-foot-length]', finder);
      const gender = query('[data-size-gender]', finder)?.value || 'men';
      const raw = footField?.value.replace(',', '.');
      const length = Number(raw);
      const error = query('[data-size-error]', finder);
      const result = query('[data-size-result]', finder);
      if (error) error.hidden = true;
      if (result) result.hidden = true;
      if (!raw || !Number.isFinite(length) || length < 15 || length > 35) {
        if (error) {error.textContent = '15–35 cm arasında geçerli bir ayak uzunluğu girin.';error.hidden = false;}
        return;
      }
      const match = (charts[gender] || []).find(row => length <= row.max + 0.0001);
      if (!match) {
        if (error) {error.textContent = 'Bu ölçü için tanımlı numara bulunamadı. Beden tablosunu kontrol edin.';error.hidden = false;}
        return;
      }
      const size = query('[data-size-result-value]', finder);
      const foot = query('[data-size-result-foot]', finder);
      if (size) size.textContent = match.size;
      if (foot) foot.textContent = `Ayakkabı • ${length.toFixed(1).replace('.', ',')} cm`;
      if (result) result.hidden = false;
    });
  }

  function initialize() {
    all('.product-page--v2[data-product-section]').forEach(section => {
      if (section.dataset.v2Initialized) return;
      section.dataset.v2Initialized = 'true';
      initializeGallery(section);
      initializeSizeTools(section);
    });
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initialize);
  else initialize();
  document.addEventListener('shopify:section:load', initialize);
})();
